// DecisionPath — 决策路径可视化
// 规格: SPEC §6 ui-kit/explainability / ADR-0013 (ECharts 5.5 按需引入) / ADR-0008 (SaMD 路径 A)
//
// SaMD 路径 A 约束 (ADR-0008):
//   仅展示客观决策路径与 AI 可解释性, 禁止诊断结论文案 (MED-02)

import { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts/core';
import type { ComposeOption } from 'echarts/core';
import { TreeChart } from 'echarts/charts';
import type { TreeSeriesOption } from 'echarts/charts';
import { TooltipComponent, TitleComponent } from 'echarts/components';
import type {
  TooltipComponentOption,
  TitleComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// ECharts 按需注册 (ADR-0013)
echarts.use([
  TreeChart,
  TooltipComponent,
  TitleComponent,
  CanvasRenderer,
]);

type DecisionPathOption = ComposeOption<
  | TreeSeriesOption
  | TooltipComponentOption
  | TitleComponentOption
>;

/** 决策节点 */
export interface DecisionNode {
  readonly id: string;
  readonly label: string;
  /** 节点匹配条件 (如 "年龄 > 65") */
  readonly condition?: string;
  /** 节点预测输出 (如 "高风险", "低风险") */
  readonly prediction?: string;
}

/** 决策边 */
export interface DecisionEdge {
  readonly from: string;
  readonly to: string;
}

export interface DecisionPathProps {
  readonly nodes: readonly DecisionNode[];
  readonly edges: readonly DecisionEdge[];
  /** 容器高度 (默认 320px) */
  readonly height?: number | string;
  /** 自定义无障碍标签 */
  readonly ariaLabel?: string;
}

/** ECharts 树节点数据 (TreeNodeOption 兼容结构) */
interface TreeNodeData {
  name: string;
  children?: TreeNodeData[];
}

/**
 * DecisionPath — 决策路径可视化
 *
 * 规格:
 *   - SPEC §6 ui-kit/explainability
 *   - ADR-0013: ECharts 5.5.x 按需引入 (TreeChart)
 *   - ADR-0008 SaMD 路径 A: 仅展示客观决策路径, 禁止诊断结论文案
 *
 * 渲染:
 *   - ECharts TreeChart 自顶向下 (orient: TB)
 *   - 节点显示 label + condition + prediction (多行)
 *   - 支持折叠/展开子树
 */
export function DecisionPath({
  nodes,
  edges,
  height = 320,
  ariaLabel,
}: DecisionPathProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  // 从 nodes/edges 构建 ECharts 树结构
  const treeData = useMemo(
    () => buildTree(nodes, edges),
    [nodes, edges],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;
    return () => {
      // StrictMode 双挂载靠 dispose 幂等 (ADR-0013)
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const option: DecisionPathOption = {
      title: {
        text: '决策路径',
        left: 'center',
        textStyle: { fontSize: 14 },
      },
      tooltip: {
        trigger: 'item',
      },
      series: [
        {
          type: 'tree',
          data: [treeData],
          top: '8%',
          left: '15%',
          bottom: '8%',
          right: '15%',
          symbolSize: 10,
          orient: 'TB', // top → bottom
          label: {
            position: 'top',
            align: 'center',
            fontSize: 12,
            lineHeight: 18,
          },
          leaves: {
            label: {
              position: 'bottom',
              align: 'center',
            },
          },
          emphasis: {
            focus: 'descendant',
          },
          expandAndCollapse: true,
          animationDuration: 550,
          animationDurationUpdate: 750,
        },
      ],
    };

    chart.setOption(option, { notMerge: false });
  }, [treeData]);

  return (
    <div
      role="img"
      aria-label={
        ariaLabel ??
        `决策路径图: ${nodes.length} 节点, ${edges.length} 边`
      }
      style={{ width: '100%', height }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {/* WCAG 2.1 SC 1.1.1: 屏幕阅读器数据表格替代 (Canvas 对 AT 不可见) */}
      <table className="sr-only">
        <caption>决策路径节点 (客观展示, 非诊断结论)</caption>
        <thead>
          <tr>
            <th scope="col">节点</th>
            <th scope="col">条件</th>
            <th scope="col">预测</th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((n) => (
            <tr key={n.id}>
              <th scope="row">{n.label}</th>
              <td>{n.condition ?? '-'}</td>
              <td>{n.prediction ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 从 nodes/edges 构建 ECharts 树结构
 *
 * 策略:
 *   - 根节点 = 没有入边的节点 (无 parent)
 *   - 多根时取第一个; 无根 (有环) 时退化为第一个节点
 *   - 节点 name 包含 label + condition + prediction (多行, \n 分隔)
 *     → SaMD 路径 A: 客观描述, 不含诊断结论文案
 */
function buildTree(
  nodes: readonly DecisionNode[],
  edges: readonly DecisionEdge[],
): TreeNodeData {
  if (nodes.length === 0) {
    return { name: '(空决策路径)' };
  }

  // 节点 name 多行展示: label + 条件 + 预测
  const formatNodeName = (n: DecisionNode): string => {
    const parts: string[] = [n.label];
    if (n.condition) parts.push(`条件: ${n.condition}`);
    if (n.prediction) parts.push(`预测: ${n.prediction}`);
    return parts.join('\n');
  };

  const nodeMap = new Map<string, TreeNodeData>();
  for (const n of nodes) {
    nodeMap.set(n.id, {
      name: formatNodeName(n),
      children: [],
    });
  }

  // 找根节点 (没有入边)
  const childIds = new Set(edges.map((e) => e.to));
  const rootIds = nodes
    .filter((n) => !childIds.has(n.id))
    .map((n) => n.id);

  // 构建父子关系
  for (const edge of edges) {
    const parent = nodeMap.get(edge.from);
    const child = nodeMap.get(edge.to);
    if (parent && child && parent.children) {
      parent.children.push(child);
    }
  }

  // 取第一个根节点; 无根时取第一个节点 (退化为单节点树)
  const rootId = rootIds[0] ?? nodes[0]?.id;
  const root = rootId ? nodeMap.get(rootId) : null;

  // 清理空 children 数组 (避免 ECharts 渲染空展开图标)
  const cleanup = (node: TreeNodeData): void => {
    if (node.children) {
      if (node.children.length === 0) {
        delete node.children;
      } else {
        for (const child of node.children) {
          cleanup(child);
        }
      }
    }
  };
  if (root) cleanup(root);

  return root ?? { name: '(空决策路径)' };
}
